// src/pages/attorney/CaseExpensesPage.tsx
// BP-10-04: Case expenses report page for attorneys

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Filter,
  Calendar,
  DollarSign,
  TrendingUp,
  FileText,
  PieChart,
} from 'lucide-react';
import { useCaseExpenses } from '../hooks/useAttorney';
import { formatCurrency } from '@/core/lib/utils';

type DateRange = '30d' | '90d' | '1y' | 'all' | 'custom';
type GroupBy = 'category' | 'month' | 'child';

export const CaseExpensesPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const [dateRange, setDateRange] = useState<DateRange>('1y');
  const [groupBy, setGroupBy] = useState<GroupBy>('category');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const { expenseReport, isLoading, exportReport, isExporting } = useCaseExpenses(caseId!, {
    date_range: dateRange,
    group_by: groupBy,
    start_date: dateRange === 'custom' ? customStart : undefined,
    end_date: dateRange === 'custom' ? customEnd : undefined,
  });

  const handleExport = (format: 'csv' | 'pdf') => {
    exportReport({ format });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage"></div>
      </div>
    );
  }

  const report = expenseReport;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={`/attorney/cases/${caseId}`}
            className="p-2 hover:bg-cream rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-warm-gray" />
          </Link>
          <div>
            <h1 className="text-2xl font-display font-semibold text-navy">Expense Report</h1>
            <p className="text-warm-gray">Case #{report?.case_number}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => handleExport('csv')}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 border border-warm-gray/20 rounded-lg hover:bg-cream transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
          >
            <FileText className="w-4 h-4" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-warm-gray" />
            <span className="text-sm text-warm-gray">Date Range:</span>
            <div className="flex gap-1">
              {(['30d', '90d', '1y', 'all', 'custom'] as DateRange[]).map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    dateRange === range
                      ? 'bg-sage text-white'
                      : 'hover:bg-cream text-navy'
                  }`}
                >
                  {range === '30d' ? '30 Days' :
                   range === '90d' ? '90 Days' :
                   range === '1y' ? '1 Year' :
                   range === 'all' ? 'All Time' : 'Custom'}
                </button>
              ))}
            </div>
          </div>

          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-3 py-1.5 text-sm border border-warm-gray/20 rounded-lg"
              />
              <span className="text-warm-gray">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-3 py-1.5 text-sm border border-warm-gray/20 rounded-lg"
              />
            </div>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <Filter className="w-4 h-4 text-warm-gray" />
            <span className="text-sm text-warm-gray">Group By:</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              className="px-3 py-1.5 text-sm border border-warm-gray/20 rounded-lg"
            >
              <option value="category">Category</option>
              <option value="month">Month</option>
              <option value="child">Child</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
          <div className="flex items-center gap-2 text-warm-gray text-sm mb-2">
            <DollarSign className="w-4 h-4" />
            Total Expenses
          </div>
          <div className="text-2xl font-display font-semibold text-navy">
            {formatCurrency(report?.total_expenses || 0)}
          </div>
          <div className="text-xs text-warm-gray mt-1">
            {report?.expense_count || 0} entries
          </div>
        </div>

        <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
          <div className="flex items-center gap-2 text-warm-gray text-sm mb-2">
            <TrendingUp className="w-4 h-4" />
            Monthly Average
          </div>
          <div className="text-2xl font-display font-semibold text-navy">
            {formatCurrency(report?.monthly_average || 0)}
          </div>
          <div className="text-xs text-warm-gray mt-1">
            over {report?.months_span || 0} months
          </div>
        </div>

        <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
          <div className="flex items-center gap-2 text-warm-gray text-sm mb-2">
            <FileText className="w-4 h-4" />
            Insurance Covered
          </div>
          <div className="text-2xl font-display font-semibold text-green-600">
            {formatCurrency(report?.insurance_covered || 0)}
          </div>
          <div className="text-xs text-warm-gray mt-1">
            {report?.insurance_percentage || 0}% of total
          </div>
        </div>

        <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
          <div className="flex items-center gap-2 text-warm-gray text-sm mb-2">
            <PieChart className="w-4 h-4" />
            Out of Pocket
          </div>
          <div className="text-2xl font-display font-semibold text-terracotta">
            {formatCurrency(report?.out_of_pocket || 0)}
          </div>
          <div className="text-xs text-warm-gray mt-1">
            {100 - (report?.insurance_percentage || 0)}% of total
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="grid grid-cols-3 gap-6">
        {/* Chart */}
        <div className="col-span-2 bg-white rounded-xl border border-warm-gray/10 p-6">
          <h3 className="font-medium text-navy mb-4">Expense Breakdown</h3>
          <div className="h-64 flex items-center justify-center text-warm-gray">
            {/* Placeholder for chart - would use Recharts */}
            <div className="text-center">
              <PieChart className="w-16 h-16 mx-auto mb-2 text-sage/30" />
              <p className="text-sm">Expense breakdown chart</p>
            </div>
          </div>
        </div>

        {/* Category List */}
        <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
          <h3 className="font-medium text-navy mb-4">By Category</h3>
          <div className="space-y-3">
            {report?.by_category?.map((cat: any) => (
              <div key={cat.category} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: cat.color || '#7A9E7E' }}
                  />
                  <span className="text-sm text-navy">{cat.category}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-navy">
                    {formatCurrency(cat.total)}
                  </div>
                  <div className="text-xs text-warm-gray">
                    {cat.percentage}%
                  </div>
                </div>
              </div>
            )) || (
              <p className="text-sm text-warm-gray">No expenses recorded</p>
            )}
          </div>
        </div>
      </div>

      {/* Expense Table */}
      <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-warm-gray/10">
          <h3 className="font-medium text-navy">All Expenses</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-warm-gray uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-warm-gray uppercase tracking-wider">
                  Insurance
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-warm-gray uppercase tracking-wider">
                  Out of Pocket
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-gray/10">
              {report?.expenses?.map((expense: any) => (
                <tr key={expense.id} className="hover:bg-cream/30">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-navy">
                    {new Date(expense.expense_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded-full bg-cream text-navy">
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-navy max-w-xs truncate">
                    {expense.description}
                  </td>
                  <td className="px-6 py-4 text-sm text-warm-gray">
                    {expense.vendor_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-navy">
                    {formatCurrency(expense.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                    {expense.insurance_amount > 0 && formatCurrency(expense.insurance_amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-terracotta">
                    {formatCurrency(expense.out_of_pocket)}
                  </td>
                </tr>
              )) || (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-warm-gray">
                    No expenses found for this period
                  </td>
                </tr>
              )}
            </tbody>
            {report?.expenses?.length > 0 && (
              <tfoot className="bg-cream/50">
                <tr>
                  <td colSpan={4} className="px-6 py-3 text-sm font-medium text-navy">
                    Total
                  </td>
                  <td className="px-6 py-3 text-right text-sm font-semibold text-navy">
                    {formatCurrency(report?.total_expenses || 0)}
                  </td>
                  <td className="px-6 py-3 text-right text-sm font-semibold text-green-600">
                    {formatCurrency(report?.insurance_covered || 0)}
                  </td>
                  <td className="px-6 py-3 text-right text-sm font-semibold text-terracotta">
                    {formatCurrency(report?.out_of_pocket || 0)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default CaseExpensesPage;

